
import React, { useEffect, useState } from 'react';
import Button from '../../ui/button';
import { useSelector } from 'react-redux';
import { getReport, getLatestAlerts } from '../../../redux/api/alertAPI';
import { getSites } from '../../../redux/api/siteAPI';
import Select from '../../ui/select';

import { useTranslation } from 'react-i18next';

const localSite = () => {
    const c = JSON.parse(localStorage.getItem('site'));
    return c ? { value: c.Site_ID, label: c.Name } : null;
};

const GenerateReportModal = ({ isOpen, onClose }) => {
    const {t} = useTranslation('common');
    const [sitesList, setSitesList] = useState(null); // State for list of sites
    const [error, setError] = useState(null); // State for error handling
    const [site, setSite] = useState(null)
    const [loadingReport, setLoadingReport] = useState(false)
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [siteAlerts, setSiteAlerts] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(true);

    const userSite = useSelector(state => state.auth.site || null);
    const userCompany = localStorage.getItem("company");

    useEffect(() => {
        const site = localSite() || userSite;
        if (site?.value) {
            setSite(site);
        }
    }, [userSite]);

    // Re-check whether the currently selected site (in this modal, which can
    // differ from the Header's own site selector) has any alerts to report —
    // mirrors the same blur/disable pattern used on the Header's button.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        const fetchSiteAlerts = async () => {
            setAlertsLoading(true);
            try {
                const { error, data } = await getLatestAlerts(site?.value || null, userCompany || null);
                if (cancelled) return;
                if (error || !Array.isArray(data)) {
                    setSiteAlerts([]);
                } else {
                    setSiteAlerts(data);
                }
            } catch (err) {
                if (!cancelled) setSiteAlerts([]);
            } finally {
                if (!cancelled) setAlertsLoading(false);
            }
        };
        fetchSiteAlerts();
        return () => { cancelled = true; };
    }, [isOpen, site, userCompany]);

    const noAlertsForSite = !alertsLoading && siteAlerts.length === 0;

    useEffect(() => {
        const fetchSites = async () => {
            try {
                const { error, data } = await getSites();
                setSitesList(data.sites);
                if (error) {
                    throw new Error(error);
                }
            } catch (err) {
                setError(err);
            }
        };
        fetchSites();
    }, []);

    const closeModal = (refresh=false) => {
        onClose(refresh);
    };
    const handleSiteSelect = (option) => {
        if (!option) {
            setSite({ label: "", value: "" });
        }
        else if (option.value) {
            setSite(option)
        }
    }

    const handleGenerateReport = async (e) => {
        e.preventDefault();
        const now = new Date()
        const formattedDate = now.toLocaleDateString('en-GB').replace(/\//g, '-'); // e.g., "21-11-2024" 
        const formattedTime = now.toLocaleTimeString('en-GB').replace(/:/g, '-'); // e.g., "13-56-00" 
        const timestamp = `${formattedDate}_${formattedTime}`;
        try {
            if (startDate && endDate && startDate > endDate) {
                throw new Error("Start date must be before end date");
            }
            setLoadingReport(true)
            const { error, data } = await getReport(site?.value || null, { startDate, endDate });
            if (error || !data) {
                throw new Error(error || 'no data returned from server');
            }
            if (!(data instanceof Blob)) {
                throw new Error('data returned from server is not a Blob');
            }
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = timestamp+'_report.pdf'; // You can customize the file name 
            document.body.appendChild(a);
            a.click();
            a.remove();
            if (error) {
                throw new Error(error);
            }
        } catch (error) {
            console.error('Error generate the report: ', error);
            alert(`failed to generate report: ${error.message}`)
        }
        finally {
            setLoadingReport(false)
        }
    };
    return (sitesList && <div data-testid="generate-report-modal" className=" grid space-x-6   max-lg:grid-cols-1 grid-cols-2 w-auto m-2 bg-white  max-md:p-3 p-8 rounded-3xl shadow-lg">
        <p className="flex justify-center col-span-2 text-xl  text-[#4C4C66] font-bold mb-10">{t('generate_report')}</p>
        <p className="flex justify-center col-span-2 text-md text-[#4C4C66] font-normal mb-4">{t('you_can_generate_a_full_report_or_select_a_site_for_a_specific_site')}{/* <span className=' font-bold'>
                {site?.label}
            </span> */}

        </p>
        {sitesList &&
            (
                site?.label ?
                    <div className="flex justify-center col-span-full align-items-center gap-5">
                        <Select
                            inputId="generate-report-site"
                            
                            isClearable={true}
                            onChange={handleSiteSelect}
                            placeholder={t('select_site')}
                            defaultValue={site}
                            options={sitesList.map(site => ({
                                label: String(site.Name),
                                value: site.Site_ID
                            }))} />
                    </div>
                    :
                    <div className="flex justify-center col-span-full items-center gap-5">
                        <Select
                            inputId="generate-report-site"

                            isClearable={true}
                            onChange={handleSiteSelect}
                            placeholder={t('select_site')}
                            options={sitesList.map(site => ({
                                label: String(site.Name),
                                value: site.Site_ID
                            }))} />
                    </div>
            )}
        {noAlertsForSite && (
            <p className="flex justify-center col-span-full text-sm font-semibold text-red-500 mt-3">
                {t('no_alerts_to_report', 'No alerts recorded for this site — nothing to include in the report.')}
            </p>
        )}
        <div className="col-span-full grid grid-cols-2 gap-4 mt-6 max-md:grid-cols-1">
            <label className="flex flex-col gap-2 text-sm font-bold text-[#4C4C66]">
                {t('start_date', 'Start date')}
                <input
                    type="date"
                    value={startDate}
                    max={endDate || undefined}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="h-11 rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm font-normal text-[#4C4C66] outline-none focus:border-[#0071BC]"
                />
            </label>
            <label className="flex flex-col gap-2 text-sm font-bold text-[#4C4C66]">
                {t('end_date', 'End date')}
                <input
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="h-11 rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm font-normal text-[#4C4C66] outline-none focus:border-[#0071BC]"
                />
            </label>
        </div>
        {(startDate || endDate) && (
            <button
                type="button"
                onClick={() => {
                    setStartDate("");
                    setEndDate("");
                }}
                className="col-span-full justify-self-center text-sm font-bold text-[#0071BC]"
            >
                {t('clear_dates', 'Clear dates')}
            </button>
        )}
        <div className='col-span-2 flex justify-center mt-10 space-x-6'>
            <Button type="reset" onClick={closeModal} size="large" color="cancel" >{t('cancel')}</Button>
            <Button
                data-testid="generate-report-submit"
                disabled={loadingReport || noAlertsForSite}
                tooltip={noAlertsForSite ? t('no_alerts_to_report', 'No alerts recorded for this site — nothing to include in the report.') : ''}
                className={`flex justify-center items-center ${noAlertsForSite ? 'blur-[1px]' : ''}`}
                onClick={handleGenerateReport}
                size="large"
            >
                {loadingReport ? (
                    <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating...</span>
                    </>
                ) : (
                    <span>{t('download')}</span>
                )}
            </Button>
        </div>
    </div>);
};

export default GenerateReportModal;